import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { resetPassword, verifyResetToken } from '../api/passwordReset';

const ResetPasswordPage: React.FC = () => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const token = searchParams.get('token');

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setError('No reset token provided');
        setVerifying(false);
        return;
      }

      try {
        const response = await verifyResetToken(token);
        if (response.valid) {
          setTokenValid(true);
        } else {
          setError('Invalid or expired reset token');
        }
      } catch (err) {
        setError('Invalid or expired reset token');
      } finally {
        setVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    if (!token) {
      setError('No reset token provided');
      return;
    }

    setLoading(true);

    try {
      const response = await resetPassword({
        token,
        new_password: newPassword,
      });
      setMessage(response.message || 'Password reset successfully');

      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  // Verifying state
  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0b0f] bg-grid-pattern relative overflow-hidden px-4">
        <div className="absolute inset-0 bg-radial-gradient pointer-events-none" />
        <div className="relative w-full max-w-md text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-400 to-amber-600 mb-4 shadow-lg shadow-amber-500/20 animate-pulse">
            <svg className="w-8 h-8 text-[#0a0b0f]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h1 className="text-2xl font-semibold text-zinc-100 mb-2">Verifying Token...</h1>
          <p className="text-zinc-500">Please wait while we verify your reset token.</p>
        </div>
      </div>
    );
  }

  // Invalid token state
  if (!tokenValid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0b0f] bg-grid-pattern relative overflow-hidden px-4">
        <div className="absolute inset-0 bg-radial-gradient pointer-events-none" />
        <div className="absolute top-20 left-20 w-64 h-64 bg-red-400/5 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-red-400/3 rounded-full blur-3xl" />

        <div className="relative w-full max-w-md">
          <div className="text-center mb-8 opacity-0 animate-slide-up stagger-1">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-red-400 to-red-600 mb-4 shadow-lg shadow-red-500/20">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h1 className="text-3xl font-semibold text-zinc-100 tracking-tight">
              Invalid Token
            </h1>
          </div>

          <div className="card-dark rounded-2xl p-8 shadow-2xl shadow-black/50 opacity-0 animate-slide-up stagger-2">
            <div className="alert-error text-center mb-6">
              <p className="font-medium">{error}</p>
              <p className="mt-2 text-sm opacity-80">
                The reset link may have expired or already been used. Please request a new password reset.
              </p>
            </div>

            <button
              onClick={() => navigate('/forgot-password')}
              className="btn-primary w-full py-3.5 rounded-xl text-base"
            >
              Request New Reset Link
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (message) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0b0f] bg-grid-pattern relative overflow-hidden px-4">
        <div className="absolute inset-0 bg-radial-gradient pointer-events-none" />
        <div className="absolute top-20 left-20 w-64 h-64 bg-emerald-400/5 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-emerald-400/3 rounded-full blur-3xl" />

        <div className="relative w-full max-w-md">
          <div className="text-center mb-8 opacity-0 animate-slide-up stagger-1">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-400 to-emerald-600 mb-4 shadow-lg shadow-emerald-500/20">
              <svg className="w-8 h-8 text-[#0a0b0f]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-3xl font-semibold text-zinc-100 tracking-tight">
              Password Reset Successfully
            </h1>
          </div>

          <div className="card-dark rounded-2xl p-8 shadow-2xl shadow-black/50 opacity-0 animate-slide-up stagger-2">
            <div className="alert-success text-center">
              <p className="font-medium">{message}</p>
              <p className="mt-2 text-sm opacity-80">
                Redirecting you to login page...
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Form state
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0a0b0f] bg-grid-pattern relative overflow-hidden px-4">
      <div className="absolute inset-0 bg-radial-gradient pointer-events-none" />
      <div className="absolute top-20 left-20 w-64 h-64 bg-amber-400/5 rounded-full blur-3xl" />
      <div className="absolute bottom-20 right-20 w-96 h-96 bg-amber-400/3 rounded-full blur-3xl" />

      <div className="relative w-full max-w-md">
        <div className="text-center mb-8 opacity-0 animate-slide-up stagger-1">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-400 to-amber-600 mb-4 shadow-lg shadow-amber-500/20">
            <svg className="w-8 h-8 text-[#0a0b0f]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
            </svg>
          </div>
          <h1 className="text-3xl font-semibold text-zinc-100 tracking-tight">
            Reset Your Password
          </h1>
          <p className="text-zinc-500 mt-2 text-sm">
            Enter your new password below
          </p>
        </div>

        <div className="card-dark rounded-2xl p-8 shadow-2xl shadow-black/50 opacity-0 animate-slide-up stagger-2">
          {error && (
            <div className="alert-error text-center mb-6">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="opacity-0 animate-slide-up stagger-3">
              <label htmlFor="newPassword" className="block text-sm font-medium text-zinc-400 mb-2">
                New Password
              </label>
              <input
                type="password"
                id="newPassword"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                placeholder="Enter new password"
                disabled={loading}
                minLength={8}
                className="input-dark w-full px-4 py-3 rounded-xl text-zinc-100 disabled:opacity-50"
              />
            </div>

            <div className="opacity-0 animate-slide-up stagger-4">
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-zinc-400 mb-2">
                Confirm Password
              </label>
              <input
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                placeholder="Confirm new password"
                disabled={loading}
                className="input-dark w-full px-4 py-3 rounded-xl text-zinc-100 disabled:opacity-50"
              />
            </div>

            <div className="opacity-0 animate-slide-up stagger-5 pt-2">
              <button
                type="submit"
                className="btn-primary w-full py-3.5 rounded-xl text-base disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                disabled={loading}
              >
                {loading ? (
                  <span className="inline-flex items-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Resetting...
                  </span>
                ) : (
                  'Reset Password'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
